<template>
  <div>

    <v-row class="mb-6">
      <v-col cols="12" sm="4">
        <div class="stat-card" style="--card-bar:#F26522;--icon-bg:rgba(242,101,34,0.10);">
          <div class="stat-card-top">
            <div class="stat-card-label">Total Revenue</div>
            <div class="stat-card-icon">
              <v-icon color="#F26522" size="20">mdi-currency-inr</v-icon>
            </div>
          </div>
          <div class="stat-card-value">₹ {{ stats.total_revenue.toLocaleString('en-IN') }}</div>
          <div class="stat-card-delta">All time collected</div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-card" style="--card-bar:#3b82f6;--icon-bg:rgba(59,130,246,0.10);">
          <div class="stat-card-top">
            <div class="stat-card-label">This Month</div>
            <div class="stat-card-icon">
              <v-icon color="#3b82f6" size="20">mdi-calendar-check-outline</v-icon>
            </div>
          </div>
          <div class="stat-card-value">₹ {{ stats.this_month_revenue.toLocaleString('en-IN') }}</div>
          <div class="stat-card-delta">Collected in {{ currentMonthName }}</div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-card" style="--card-bar:#8b5cf6;--icon-bg:rgba(139,92,246,0.10);">
          <div class="stat-card-top">
            <div class="stat-card-label">Last Month</div>
            <div class="stat-card-icon">
              <v-icon color="#8b5cf6" size="20">mdi-history</v-icon>
            </div>
          </div>
          <div class="stat-card-value">₹ {{ stats.last_month_revenue.toLocaleString('en-IN') }}</div>
          <div class="stat-card-delta">Collected in {{ lastMonthName }}</div>
        </div>
      </v-col>
    </v-row>

    <PremiumTable title="Payments" subtitle="View all subscription payment transactions." :headers="headers"
      :items="filteredPayments" :loading="loading" show-search search-placeholder="Search payments...">
      <template v-slot:filters>
        <PremiumDateRangePicker v-model="dateRange" @change="getPayments" />
      </template>
      
      <template v-slot:item.plan_name="{ item }">
        <span style="font-weight:600;color:var(--accent);white-space:nowrap;">
          {{ item.plan_name || '—' }}
        </span>
      </template>

      <template v-slot:item.society_name="{ item }">
        <TruncatedCell :text="item.society_name || '—'" max-width="160px" />
      </template>

      <template v-slot:item.contact_person_name="{ item }">
        <div style="display:flex;flex-direction:column;min-width:0;">
          <span style="font-weight:500;white-space:nowrap;">
            {{ item.contact_person_name || '—' }}
          </span>
        </div>
      </template>

      <template v-slot:item.purchase_date="{ item }">
        <span style="white-space:nowrap;">{{ formatDate(item.purchase_date) }}</span>
      </template>

      <template v-slot:item.amount="{ item }">
        <span style="font-weight:600;color:var(--text-primary);white-space:nowrap;">
          ₹ {{ item.amount.toLocaleString('en-IN') }}
        </span>
      </template>

      <template v-slot:item.status="{ item }">
        <span class="status-pill" :class="getStatusClass(item.status)">
          <span class="status-dot"></span>
          {{ getStatusText(item.status) }}
        </span>
      </template>

      <template v-slot:item.transaction_id="{ item }">
        <v-tooltip top open-delay="400" content-class="tooltip-top" :disabled="$vuetify.breakpoint.smAndDown">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="mono-text">{{ item.transaction_id }}</span>
          </template>
          <span>{{ item.transaction_id }}</span>
        </v-tooltip>
      </template>
    </PremiumTable>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import moment from 'moment';
import PremiumTable from '../common/PremiumTable.vue';
import TruncatedCell from '../common/TruncatedCell.vue';
import PremiumDateRangePicker from '../common/PremiumDateRangePicker.vue';

export default {
  name: 'PaymentsPage',
  components: { PremiumTable, TruncatedCell, PremiumDateRangePicker },

  data() {
    return {
      loading: false,
      payments: [],
      dateRange: [
        moment().subtract(29, 'days').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD')
      ],

      headers: [
        { text: 'Transaction ID', value: 'transaction_id', width: '20%' },
        { text: 'Purchase Date', value: 'purchase_date', width: '13%' },
        { text: 'Amount', value: 'amount', width: '10%' },
        { text: 'Plan', value: 'plan_name', width: '12%' },
        { text: 'Member Count', value: 'total_count', width: '12%' },
        { text: 'Status', value: 'status', width: '12%' },
        { text: 'Society Name', value: 'society_name', width: '16%' },
      ],
      stats: {
        total_revenue: 0,
        this_month_revenue: 0,
        last_month_revenue: 0
      }
    };
  },

  computed: {
    currentMonthName() { return moment().format('MMMM'); },
    lastMonthName() { return moment().subtract(1, 'month').format('MMMM'); },

    filteredPayments() {
      if (!this.dateRange || this.dateRange.length < 2) return this.payments;
      const start = moment(this.dateRange[0]).startOf('day');
      const end = moment(this.dateRange[1]).endOf('day');
      return this.payments.filter(p => {
        const d = moment(p.purchase_date);
        return d.isBetween(start, end, null, '[]');
      });
    }
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    formatDate(d) { return moment(d).format('DD MMM, YYYY'); },

    getStatusClass(status) {
      if (!status) return 'pill-failed';
      const s = status.toLowerCase();
      if (s === 'success') return 'pill-success';
      if (s === 'pending') return 'pill-pending';
      if (s === 'failed') return 'pill-failed';
      if (s === 'user_dropped') return 'pill-dropped';
      return 'pill-failed';
    },

    getStatusText(status) {
      if (!status) return '—';
      return status.replace('_', ' ');
    },

    getPayments() {
      this.loading = true;
      const params = {};
      if (this.dateRange && this.dateRange.length === 2) {
        params.start_date = this.dateRange[0];
        params.end_date = this.dateRange[1];
      }

      this.$axios.get('superadmin/payment', { params }).then(res => {
        this.loading = false;
        const data = res.data;
        if (data.status) {
          this.payments = data.payment || [];
          this.stats.total_revenue = data.total_revenue || 0;
          this.stats.this_month_revenue = data.this_month_revenue || 0;
          this.stats.last_month_revenue = data.last_month_revenue || 0;
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message });
        }
      }).catch(() => { this.loading = false; });
    },
  },

  mounted() {
    this.getPayments();
  },
};
</script>

<style scoped>
.mono-text {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 11.5px;
  color: var(--text-secondary, #64748b);
  background: var(--bg-primary, #f1f5f9);
  padding: 3px 7px;
  border-radius: 5px;
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.07));
  white-space: nowrap;
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

/* ─── CUSTOM PILL DESIGN (LIKE GATE PASS) ─── */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
  white-space: nowrap;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

/* Success - Green */
.pill-success {
  background: rgba(16, 185, 129, 0.1) !important;
  color: #10b981 !important;
}
.pill-success .status-dot {
  background-color: #10b981;
}

/* Pending - Yellow/Amber */
.pill-pending {
  background: rgba(245, 158, 11, 0.1) !important;
  color: #f59e0b !important;
}
.pill-pending .status-dot {
  background-color: #f59e0b;
}

/* Failed - Red */
.pill-failed {
  background: rgba(239, 68, 68, 0.1) !important;
  color: #ef4444 !important;
}
.pill-failed .status-dot {
  background-color: #ef4444;
}

/* User Dropped - Blue */
.pill-dropped {
  background: rgba(59, 130, 246, 0.1) !important;
  color: #3b82f6 !important;
}
.pill-dropped .status-dot {
  background-color: #3b82f6;
}
</style>